"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Pairing = void 0;
const tslib_1 = require("tslib");
const events_1 = require("events");
const pino_utils_1 = require("@pedrouid/pino-utils");
const types_1 = require("@walletconnect/types");
const utils_1 = require("@walletconnect/utils");
const utils_2 = require("@json-rpc-tools/utils");
const subscription_1 = require("./subscription");
const history_1 = require("./history");
const constants_1 = require("../constants");
class Pairing extends types_1.IPairing {
    constructor(client, logger) {
        super(client, logger);
        this.client = client;
        this.logger = logger;
        this.events = new events_1.EventEmitter();
        this.context = constants_1.PAIRING_CONTEXT;
        this.logger = pino_utils_1.generateChildLogger(logger, this.context);
        this.pending = new subscription_1.Subscription(client, this.logger, constants_1.PAIRING_STATUS.pending, false);
        this.settled = new subscription_1.Subscription(client, this.logger, constants_1.PAIRING_STATUS.settled, true);
        this.history = new history_1.JsonRpcHistory(client, this.logger);
        this.registerEventListeners();
    }
    init() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.logger.trace(`Initialized`);
            yield this.pending.init();
            yield this.settled.init();
            yield this.history.init();
        });
    }
    get(topic) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.settled.get(topic);
        });
    }
    ping(topic, timeout) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const request = { method: constants_1.PAIRING_JSONRPC.ping, params: {} };
            return this.request({ topic, request, timeout: timeout || constants_1.THIRTY_SECONDS * 1000 });
        });
    }
    send(topic, payload) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const pairing = yield this.settled.get(topic);
            const encryptKeys = {
                sharedKey: pairing.sharedKey,
                publicKey: pairing.self.publicKey,
            };
            if (utils_2.isJsonRpcRequest(payload)) {
                if (!Object.values(constants_1.PAIRING_JSONRPC).includes(payload.method)) {
                    if (!pairing.permissions.jsonrpc.methods.includes(payload.method)) {
                        const error = utils_1.getError(utils_1.ERROR.UNAUTHORIZED_JSON_RPC_METHOD, {
                            method: payload.method,
                        });
                        this.logger.error(error.message);
                        throw new Error(error.message);
                    }
                    yield this.history.set(topic, payload);
                    payload = utils_2.formatJsonRpcRequest(constants_1.PAIRING_JSONRPC.payload, {
                        request: { method: payload.method, params: payload.params },
                    }, payload.id);
                }
            }
            else {
                yield this.history.update(topic, payload);
            }
            yield this.client.relayer.publish(pairing.topic, payload, {
                relay: pairing.relay,
                encryptKeys,
            });
        });
    }
    get length() {
        return this.settled.length;
    }
    get topics() {
        return this.settled.topics;
    }
    get values() {
        return this.settled.values.map(x => x.data);
    }
    create(params) {
        return new Promise((resolve, reject) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.logger.debug(`Create Pairing`);
            this.logger.trace({ type: "method", method: "create", params });
            const maxTimeout = (params === null || params === void 0 ? void 0 : params.timeout) || constants_1.FIVE_MINUTES * 1000;
            const timeout = setTimeout(() => {
                const error = utils_1.getError(utils_1.ERROR.SETTLE_TIMEOUT, {
                    context: this.context,
                    timeout: maxTimeout,
                });
                this.logger.error(error.message);
                reject(error.message);
            }, maxTimeout);
            let pending;
            try {
                pending = yield this.propose(params);
            }
            catch (e) {
                clearTimeout(timeout);
                return reject(e);
            }
            this.pending.on(constants_1.SUBSCRIPTION_EVENTS.updated, (updatedEvent) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                if (pending.topic !== updatedEvent.data.topic)
                    return;
                if (utils_1.isPairingResponded(updatedEvent.data)) {
                    const outcome = updatedEvent.data.outcome;
                    clearTimeout(timeout);
                    if (utils_1.isPairingFailed(outcome)) {
                        try {
                            yield this.pending.delete(pending.topic, outcome.reason);
                        }
                        catch (e) {
                            return reject(e);
                        }
                        reject(new Error(outcome.reason.message));
                    }
                    else {
                        try {
                            const pairing = yield this.settled.get(outcome.topic);
                            const reason = utils_1.getError(utils_1.ERROR.SETTLED, { context: this.context });
                            yield this.pending.delete(pending.topic, reason);
                            resolve(pairing);
                        }
                        catch (e) {
                            return reject(e);
                        }
                    }
                }
            }));
        }));
    }
    respond(params) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.logger.debug(`Respond Pairing`);
            this.logger.trace({ type: "method", method: "respond", params });
            const { approved, proposal } = params;
            const self = utils_1.generateKeyPair();
            if (approved) {
                try {
                    const responder = {
                        publicKey: self.publicKey,
                    };
                    const expiry = Date.now() + proposal.ttl * 1000;
                    const controller = proposal.proposer.controller
                        ? { publicKey: proposal.proposer.publicKey }
                        : { publicKey: self.publicKey };
                    const pairing = yield this.settle({
                        relay: proposal.relay,
                        self,
                        peer: { publicKey: proposal.proposer.publicKey },
                        permissions: Object.assign(Object.assign({}, proposal.permissions), { controller }),
                        state: {},
                        ttl: proposal.ttl,
                        expiry,
                    });
                    const outcome = {
                        topic: pairing.topic,
                        relay: pairing.relay,
                        responder,
                        expiry,
                        state: {},
                    };
                    const pending = {
                        status: constants_1.PAIRING_STATUS.responded,
                        topic: proposal.topic,
                        relay: proposal.relay,
                        self,
                        proposal,
                        outcome,
                    };
                    yield this.pending.set(pending.topic, pending, { relay: pending.relay });
                    return pending;
                }
                catch (e) {
                    const reason = utils_1.getError(utils_1.ERROR.GENERIC, { message: e.message });
                    const outcome = { reason };
                    const pending = {
                        status: constants_1.PAIRING_STATUS.responded,
                        topic: proposal.topic,
                        relay: proposal.relay,
                        self,
                        proposal,
                        outcome,
                    };
                    yield this.pending.set(pending.topic, pending, { relay: pending.relay });
                    return pending;
                }
            }
            else {
                const defaultReason = utils_1.getError(utils_1.ERROR.NOT_APPROVED, { context: this.context });
                const outcome = { reason: (params === null || params === void 0 ? void 0 : params.reason) || defaultReason };
                const pending = {
                    status: constants_1.PAIRING_STATUS.responded,
                    topic: proposal.topic,
                    relay: proposal.relay,
                    self,
                    proposal,
                    outcome,
                };
                yield this.pending.set(pending.topic, pending, { relay: pending.relay });
                return pending;
            }
        });
    }
    upgrade(params) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.logger.info(`Upgrade Pairing`);
            this.logger.trace({ type: "method", method: "upgrade", params });
            const pairing = yield this.settled.get(params.topic);
            const participant = { publicKey: pairing.self.publicKey };
            const upgrade = yield this.handleUpgrade(params.topic, params, participant);
            const request = utils_2.formatJsonRpcRequest(constants_1.PAIRING_JSONRPC.upgrade, upgrade);
            yield this.send(pairing.topic, request);
            return pairing;
        });
    }
    update(params) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.logger.debug(`Update Pairing`);
            this.logger.trace({ type: "method", method: "update", params });
            const pairing = yield this.settled.get(params.topic);
            const participant = { publicKey: pairing.self.publicKey };
            const update = yield this.handleUpdate(params.topic, params, participant);
            const request = utils_2.formatJsonRpcRequest(constants_1.PAIRING_JSONRPC.update, update);
            yield this.send(pairing.topic, request);
            return pairing;
        });
    }
    request(params) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => tslib_1.__awaiter(this, void 0, void 0, function* () {
                const request = utils_2.formatJsonRpcRequest(params.request.method, params.request.params);
                const maxTimeout = (params === null || params === void 0 ? void 0 : params.timeout) || constants_1.FIVE_MINUTES * 1000;
                const timeout = setTimeout(() => {
                    const error = utils_1.getError(utils_1.ERROR.JSONRPC_REQUEST_TIMEOUT, {
                        method: request.method,
                        timeout: maxTimeout,
                    });
                    this.logger.error(error.message);
                    reject(error.message);
                }, maxTimeout);
                this.events.on(constants_1.PAIRING_EVENTS.response, (responseEvent) => {
                    if (params.topic !== responseEvent.topic)
                        return;
                    const response = responseEvent.response;
                    if (response.id !== request.id)
                        return;
                    clearTimeout(timeout);
                    if (utils_2.isJsonRpcError(response)) {
                        const errorMessage = response.error.message;
                        this.logger.error(errorMessage);
                        return reject(new Error(errorMessage));
                    }
                    return resolve(response.result);
                });
                try {
                    yield this.send(params.topic, request);
                }
                catch (e) {
                    clearTimeout(timeout);
                    return reject(e);
                }
            }));
        });
    }
    delete(params) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.logger.debug(`Delete Pairing`);
            this.logger.trace({ type: "method", method: "delete", params });
            yield this.settled.delete(params.topic, params.reason);
        });
    }
    on(event, listener) {
        this.events.on(event, listener);
    }
    once(event, listener) {
        this.events.once(event, listener);
    }
    off(event, listener) {
        this.events.off(event, listener);
    }
    removeListener(event, listener) {
        this.events.removeListener(event, listener);
    }
    propose(params) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.logger.debug(`Propose Pairing`);
            this.logger.trace({ type: "method", method: "propose", params });
            const relay = (params === null || params === void 0 ? void 0 : params.relay) || { protocol: constants_1.RELAYER_DEFAULT_PROTOCOL };
            const topic = utils_1.generateRandomBytes32();
            const self = utils_1.generateKeyPair();
            const proposer = {
                publicKey: self.publicKey,
                controller: this.client.controller,
            };
            const uri = utils_1.formatUri({
                protocol: this.client.protocol,
                version: this.client.version,
                topic: topic,
                publicKey: proposer.publicKey,
                controller: proposer.controller,
                relay: relay,
            });
            const signal = {
                method: constants_1.PAIRING_SIGNAL_METHOD_URI,
                params: { uri },
            };
            const permissions = {
                jsonrpc: {
                    methods: [constants_1.SESSION_JSONRPC.propose],
                },
            };
            const proposal = {
                relay,
                topic,
                proposer,
                signal,
                permissions,
                ttl: constants_1.PAIRING_DEFAULT_TTL,
            };
            const pending = {
                status: constants_1.PAIRING_STATUS.proposed,
                topic: proposal.topic,
                relay: proposal.relay,
                self,
                proposal,
            };
            yield this.pending.set(pending.topic, pending, { relay });
            return pending;
        });
    }
    settle(params) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.logger.debug(`Settle Pairing`);
            this.logger.trace({ type: "method", method: "settle", params });
            const sharedKey = utils_1.deriveSharedKey(params.self.privateKey, params.peer.publicKey);
            const topic = yield utils_1.sha256(sharedKey);
            const pairing = {
                topic,
                relay: params.relay,
                sharedKey,
                self: params.self,
                peer: params.peer,
                permissions: params.permissions,
                expiry: params.expiry,
                state: params.state,
            };
            const decryptKeys = {
                sharedKey,
            };
            yield this.settled.set(pairing.topic, pairing, {
                relay: pairing.relay,
                expiry: pairing.expiry,
                decryptKeys,
            });
            return pairing;
        });
    }
    onResponse(payloadEvent) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const { topic, payload } = payloadEvent;
            this.logger.debug(`Receiving Pairing response`);
            this.logger.trace({ type: "method", method: "onResponse", topic, payload });
            const request = payload;
            const pending = yield this.pending.get(topic);
            let error;
            if (!utils_1.isPairingFailed(request.params)) {
                try {
                    const controller = pending.proposal.proposer.controller
                        ? { publicKey: pending.proposal.proposer.publicKey }
                        : { publicKey: request.params.responder.publicKey };
                    const pairing = yield this.settle({
                        relay: pending.relay,
                        self: pending.self,
                        peer: { publicKey: request.params.responder.publicKey },
                        permissions: Object.assign(Object.assign({}, pending.proposal.permissions), { controller }),
                        ttl: pending.proposal.ttl,
                        expiry: request.params.expiry,
                        state: {},
                    });
                    yield this.pending.update(topic, {
                        status: constants_1.PAIRING_STATUS.responded,
                        outcome: {
                            topic: pairing.topic,
                            relay: pairing.relay,
                            responder: request.params.responder,
                            expiry: pairing.expiry,
                            state: {},
                        },
                    });
                }
                catch (e) {
                    this.logger.error(e);
                    error = utils_1.getError(utils_1.ERROR.GENERIC, { message: e.message });
                    yield this.pending.update(topic, {
                        status: constants_1.PAIRING_STATUS.responded,
                        outcome: { reason: error },
                    });
                }
                const response = typeof error === "undefined"
                    ? utils_2.formatJsonRpcResult(request.id, true)
                    : utils_2.formatJsonRpcError(request.id, error);
                yield this.client.relayer.publish(pending.topic, response, { relay: pending.relay });
            }
            else {
                this.logger.error(request.params.reason);
                yield this.pending.update(topic, {
                    status: constants_1.PAIRING_STATUS.responded,
                    outcome: { reason: request.params.reason },
                });
            }
        });
    }
    onAcknowledge(payloadEvent) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const { topic, payload } = payloadEvent;
            this.logger.debug(`Receiving Pairing acknowledge`);
            this.logger.trace({ type: "method", method: "onAcknowledge", topic, payload });
            const response = payload;
            const pending = yield this.pending.get(topic);
            if (!utils_1.isPairingResponded(pending))
                return;
            if (utils_2.isJsonRpcError(response) && !utils_1.isPairingFailed(pending.outcome)) {
                yield this.settled.delete(pending.outcome.topic, response.error);
            }
            const reason = utils_1.getError(utils_1.ERROR.RESPONSE_ACKNOWLEDGED, { context: this.context });
            yield this.pending.delete(topic, reason);
        });
    }
    onMessage(payloadEvent) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const { topic, payload } = payloadEvent;
            this.logger.debug(`Receiving Pairing message`);
            this.logger.trace({ type: "method", method: "onMessage", topic, payload });
            if (utils_2.isJsonRpcRequest(payload)) {
                const request = payload;
                const pairing = yield this.settled.get(payloadEvent.topic);
                let error;
                switch (request.method) {
                    case constants_1.PAIRING_JSONRPC.payload:
                        yield this.onPayload(payloadEvent);
                        break;
                    case constants_1.PAIRING_JSONRPC.update:
                        yield this.onUpdate(payloadEvent);
                        break;
                    case constants_1.PAIRING_JSONRPC.upgrade:
                        yield this.onUpgrade(payloadEvent);
                        break;
                    case constants_1.PAIRING_JSONRPC.delete:
                        yield this.settled.delete(pairing.topic, request.params.reason);
                        break;
                    case constants_1.PAIRING_JSONRPC.ping:
                        yield this.send(pairing.topic, utils_2.formatJsonRpcResult(request.id, false));
                        break;
                    default:
                        error = utils_1.getError(utils_1.ERROR.UNKNOWN_JSONRPC_METHOD, { method: request.method });
                        this.logger.error(error.message);
                        yield this.send(pairing.topic, utils_2.formatJsonRpcError(request.id, error));
                        break;
                }
            }
            else {
                this.onPayloadEvent(payloadEvent);
            }
        });
    }
    onPayload(payloadEvent) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const { topic, payload } = payloadEvent;
            if (utils_2.isJsonRpcRequest(payload)) {
                const { id, params } = payload;
                const request = utils_2.formatJsonRpcRequest(params.request.method, params.request.params, id);
                const pairing = yield this.settled.get(topic);
                if (!pairing.permissions.jsonrpc.methods.includes(request.method)) {
                    const error = utils_1.getError(utils_1.ERROR.UNAUTHORIZED_JSON_RPC_METHOD, {
                        method: request.method,
                    });
                    this.logger.error(error.message);
                    throw new Error(error.message);
                }
                const pairingPayloadEvent = {
                    topic,
                    payload: request,
                };
                this.logger.debug(`Receiving Pairing payload`);
                this.logger.trace(Object.assign({ type: "method", method: "onPayload" }, pairingPayloadEvent));
                this.onPayloadEvent(pairingPayloadEvent);
            }
            else {
                const pairingPayloadEvent = {
                    topic,
                    payload,
                };
                this.logger.debug(`Receiving Pairing payload`);
                this.logger.trace(Object.assign({ type: "method", method: "onPayload" }, pairingPayloadEvent));
                this.onPayloadEvent(pairingPayloadEvent);
            }
        });
    }
    onUpdate(payloadEvent) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const { topic, payload } = payloadEvent;
            this.logger.debug(`Receiving Pairing update`);
            this.logger.trace({ type: "method", method: "onUpdate", topic, payload });
            const request = payloadEvent.payload;
            const pairing = yield this.settled.get(payloadEvent.topic);
            try {
                const participant = { publicKey: pairing.peer.publicKey };
                yield this.handleUpdate(topic, request.params, participant);
                const response = utils_2.formatJsonRpcResult(request.id, true);
                yield this.send(pairing.topic, response);
            }
            catch (e) {
                this.logger.error(e);
                const response = utils_2.formatJsonRpcError(request.id, e.message);
                yield this.send(pairing.topic, response);
            }
        });
    }
    onUpgrade(payloadEvent) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const { topic, payload } = payloadEvent;
            this.logger.debug(`Receiving Pairing upgrade`);
            this.logger.trace({ type: "method", method: "onUpgrade", topic, payload });
            const request = payloadEvent.payload;
            const pairing = yield this.settled.get(payloadEvent.topic);
            try {
                const participant = { publicKey: pairing.peer.publicKey };
                yield this.handleUpgrade(topic, request.params, participant);
                const response = utils_2.formatJsonRpcResult(request.id, true);
                yield this.send(pairing.topic, response);
            }
            catch (e) {
                this.logger.error(e);
                const response = utils_2.formatJsonRpcError(request.id, e.message);
                yield this.send(pairing.topic, response);
            }
        });
    }
    handleUpdate(topic, params, participant) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const pairing = yield this.settled.get(topic);
            let update;
            if (typeof params.state !== "undefined") {
                const state = pairing.state;
                if (participant.publicKey !== pairing.permissions.controller.publicKey) {
                    const error = utils_1.getError(utils_1.ERROR.UNAUTHORIZED_UPDATE_REQUEST, { context: this.context });
                    this.logger.error(error.message);
                    throw new Error(error.message);
                }
                state.metadata = params.state.metadata || state.metadata;
                update = { state };
            }
            else {
                const error = utils_1.getError(utils_1.ERROR.INVALID_UPDATE_REQUEST, { context: this.context });
                this.logger.error(error.message);
                throw new Error(error.message);
            }
            yield this.settled.update(pairing.topic, pairing);
            return update;
        });
    }
    handleUpgrade(topic, params, participant) {
        var _a;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const pairing = yield this.settled.get(topic);
            let upgrade = { permissions: {} };
            if (participant.publicKey !== pairing.permissions.controller.publicKey) {
                const error = utils_1.getError(utils_1.ERROR.UNAUTHORIZED_UPGRADE_REQUEST, { context: this.context });
                this.logger.error(error.message);
                throw new Error(error.message);
            }
            const permissions = {
                jsonrpc: {
                    methods: [
                        ...pairing.permissions.jsonrpc.methods,
                        ...(((_a = params.permissions.jsonrpc) === null || _a === void 0 ? void 0 : _a.methods) || []),
                    ],
                },
            };
            upgrade = { permissions };
            pairing.permissions = Object.assign(Object.assign({}, permissions), { controller: pairing.permissions.controller });
            yield this.settled.update(pairing.topic, pairing);
            return upgrade;
        });
    }
    shouldIgnorePayloadEvent(payloadEvent) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const { topic, payload } = payloadEvent;
            if (!this.settled.subscriptions.has(topic))
                return true;
            let exists = false;
            try {
                exists = yield this.history.exists(topic, payload.id);
            }
            catch (e) {
            }
            return exists;
        });
    }
    onPayloadEvent(payloadEvent) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const { topic, payload } = payloadEvent;
            if (utils_2.isJsonRpcRequest(payload)) {
                if (yield this.shouldIgnorePayloadEvent(payloadEvent))
                    return;
                yield this.history.set(topic, payload);
            }
            else {
                yield this.history.update(topic, payload);
            }
            if (utils_2.isJsonRpcRequest(payload)) {
                const requestEvent = { topic, request: payload };
                this.logger.info(`Emitting ${constants_1.PAIRING_EVENTS.request}`);
                this.logger.debug({ type: "event", event: constants_1.PAIRING_EVENTS.request, data: requestEvent });
                this.events.emit(constants_1.PAIRING_EVENTS.request, requestEvent);
            }
            else {
                const responseEvent = { topic, response: payload };
                this.logger.info(`Emitting ${constants_1.PAIRING_EVENTS.response}`);
                this.logger.debug({ type: "event", event: constants_1.PAIRING_EVENTS.response, data: responseEvent });
                this.events.emit(constants_1.PAIRING_EVENTS.response, responseEvent);
            }
        });
    }
    onPendingPayloadEvent(event) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (utils_2.isJsonRpcRequest(event.payload)) {
                switch (event.payload.method) {
                    case constants_1.PAIRING_JSONRPC.approve:
                    case constants_1.PAIRING_JSONRPC.reject:
                        this.onResponse(event);
                        break;
                    default:
                        break;
                }
            }
            else {
                this.onAcknowledge(event);
            }
        });
    }
    onPendingStatusEvent(event) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const pending = event.data;
            if (utils_1.isPairingResponded(pending)) {
                this.logger.info(`Emitting ${constants_1.PAIRING_EVENTS.responded}`);
                this.logger.debug({ type: "event", event: constants_1.PAIRING_EVENTS.responded, data: pending });
                this.events.emit(constants_1.PAIRING_EVENTS.responded, pending);
                if (!utils_1.isSubscriptionUpdatedEvent(event)) {
                    const method = !utils_1.isPairingFailed(pending.outcome)
                        ? constants_1.PAIRING_JSONRPC.approve
                        : constants_1.PAIRING_JSONRPC.reject;
                    const request = utils_2.formatJsonRpcRequest(method, pending.outcome);
                    yield this.client.relayer.publish(pending.topic, request, { relay: pending.relay });
                }
            }
            else {
                this.logger.info(`Emitting ${constants_1.PAIRING_EVENTS.proposed}`);
                this.logger.debug({ type: "event", event: constants_1.PAIRING_EVENTS.proposed, data: pending });
                this.events.emit(constants_1.PAIRING_EVENTS.proposed, pending);
            }
        });
    }
    registerEventListeners() {
        this.pending.on(constants_1.SUBSCRIPTION_EVENTS.payload, (payloadEvent) => this.onPendingPayloadEvent(payloadEvent));
        this.pending.on(constants_1.SUBSCRIPTION_EVENTS.created, (createdEvent) => this.onPendingStatusEvent(createdEvent));
        this.pending.on(constants_1.SUBSCRIPTION_EVENTS.updated, (updatedEvent) => this.onPendingStatusEvent(updatedEvent));
        this.settled.on(constants_1.SUBSCRIPTION_EVENTS.payload, (payloadEvent) => this.onMessage(payloadEvent));
        this.settled.on(constants_1.SUBSCRIPTION_EVENTS.created, (createdEvent) => {
            const pairing = createdEvent.data;
            this.logger.info(`Emitting ${constants_1.PAIRING_EVENTS.settled}`);
            this.logger.debug({ type: "event", event: constants_1.PAIRING_EVENTS.settled, data: pairing });
            this.events.emit(constants_1.PAIRING_EVENTS.settled, pairing);
        });
        this.settled.on(constants_1.SUBSCRIPTION_EVENTS.updated, (updatedEvent) => {
            const pairing = updatedEvent.data;
            this.logger.info(`Emitting ${constants_1.PAIRING_EVENTS.updated}`);
            this.logger.debug({ type: "event", event: constants_1.PAIRING_EVENTS.updated, data: pairing });
            this.events.emit(constants_1.PAIRING_EVENTS.updated, pairing);
        });
        this.settled.on(constants_1.SUBSCRIPTION_EVENTS.deleted, (deletedEvent) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            const pairing = deletedEvent.data;
            this.logger.info(`Emitting ${constants_1.PAIRING_EVENTS.deleted}`);
            this.logger.debug({ type: "event", event: constants_1.PAIRING_EVENTS.deleted, data: pairing });
            this.events.emit(constants_1.PAIRING_EVENTS.deleted, pairing);
            const request = utils_2.formatJsonRpcRequest(constants_1.PAIRING_JSONRPC.delete, {
                reason: deletedEvent.reason,
            });
            yield this.history.delete(pairing.topic);
            yield this.client.relayer.publish(pairing.topic, request, { relay: pairing.relay });
        }));
    }
}
exports.Pairing = Pairing;
//# sourceMappingURL=pairing.js.map